import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth, API, authFetch } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Mic,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  BookOpen,
  Lightbulb,
  Wrench,
  FileText,
  Send,
  Loader2,
  Download,
  Copy,
  Target,
  Headphones,
  Megaphone,
  DollarSign,
  Home,
  Info,
  ExternalLink,
  Play,
  Calendar,
  Video,
  Clock,
  Bell
} from "lucide-react";

const WEEK_ICONS = {
  1: Target,
  2: Wrench,
  3: FileText,
  4: Headphones,
  5: Megaphone,
  6: DollarSign
};

export function PodcastWeekPage() {
  const { week } = useParams();
  const weekNum = parseInt(week);
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [weekData, setWeekData] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [generatedOutput, setGeneratedOutput] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [activeTab, setActiveTab] = useState("content");
  const [meetingData, setMeetingData] = useState(null);
  const [schedulingMeeting, setSchedulingMeeting] = useState(false);
  
  // AI tool inputs
  const [toolInputs, setToolInputs] = useState({});

  useEffect(() => {
    fetchWeekData();
  }, [weekNum]);

  const fetchWeekData = async () => {
    setLoading(true);
    try {
      const [weekRes, enrollRes, meetingRes] = await Promise.all([
        authFetch(`${API}/podcast/curriculum/${weekNum}`),
        authFetch(`${API}/podcast/enrollment`),
        authFetch(`${API}/podcast/meeting-status`)
      ]);

      if (weekRes.ok) {
        const data = await weekRes.json();
        setWeekData(data);
      } else {
        const error = await weekRes.json();
        toast.error(error.detail || "Failed to load week content");
        navigate("/podcast");
        return;
      }

      if (enrollRes.ok) {
        const data = await enrollRes.json();
        setEnrollment(data);
      }
      
      if (meetingRes.ok) {
        const data = await meetingRes.json();
        setMeetingData(data);
      }
    } catch (_error) {
      console.error("Error fetching week data:", _error);
      toast.error("Failed to load content");
      navigate("/podcast");
    } finally {
      setLoading(false);
    }
  };

  const [selectedTool, setSelectedTool] = useState(null);

  const handleGenerate = async (toolOverride = null) => {
    const toolToUse = toolOverride || selectedTool || weekData?.ai_tool;
    if (!toolToUse) return;
    
    setGenerating(true);
    try {
      const toolEndpoints = {
        niche_finder: "niche-finder",
        episode_title: "episode-titles",
        show_notes: "show-notes",
        marketing_plan: "marketing-plan",
        monetization: "monetization",
        guest_outreach: "guest-outreach"
      };
      
      const endpoint = toolEndpoints[toolToUse];
      if (!endpoint) {
        toast.error("AI tool not available for this week");
        return;
      }

      const response = await authFetch(`${API}/podcast/generate/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(toolInputs)
      });

      if (response.ok) {
        const data = await response.json();
        setGeneratedOutput(data);
        toast.success("Content generated!");
        setActiveTab("output");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to generate content");
      }
    } catch (_error) {
      toast.error("Failed to generate content");
    } finally {
      setGenerating(false);
    }
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const response = await authFetch(`${API}/podcast/curriculum/${weekNum}/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.graduated) {
          toast.success("Congratulations! You've completed the program!");
          navigate("/podcast");
        } else {
          toast.success(`Week ${weekNum} completed! Week ${data.next_week} unlocked!`);
          navigate("/podcast");
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to complete week");
      }
    } catch (_error) {
      toast.error("Failed to complete week");
    } finally {
      setCompleting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const downloadOutput = () => {
    if (!generatedOutput) return;
    const text = JSON.stringify(generatedOutput, null, 2);
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `podcast_week_${weekNum}_output.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded!");
  };

  const handleBookMeeting = () => {
    if (meetingData?.booking_url) {
      window.open(meetingData.booking_url, '_blank');
    }
  };

  const handleMarkScheduled = async () => {
    setSchedulingMeeting(true);
    try {
      const response = await authFetch(`${API}/podcast/meeting/${weekNum}/schedule`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notes: "Scheduled via Square Appointments" })
      });
      
      if (response.ok) {
        toast.success("Meeting marked as scheduled! Your consultant will be notified.");
        // Refresh meeting data
        const meetingRes = await authFetch(`${API}/podcast/meeting-status`);
        if (meetingRes.ok) {
          setMeetingData(await meetingRes.json());
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update meeting status");
      }
    } catch (_error) {
      toast.error("Failed to update meeting status");
    } finally {
      setSchedulingMeeting(false);
    }
  };

  // Get current week's meeting status
  const currentMeetingStatus = meetingData?.meetings?.[weekNum]?.status || "pending";
  const isMeetingCompleted = currentMeetingStatus === "completed";
  const isMeetingScheduled = currentMeetingStatus === "scheduled";

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-purple-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!weekData) {
    return null;
  }

  const Icon = WEEK_ICONS[weekNum] || BookOpen;
  const isCompleted = enrollment?.completed_weeks?.includes(weekNum);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate("/podcast")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-neutral-200" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium text-neutral-900">Week {weekNum}</p>
                  <p className="text-xs text-neutral-500">{weekData.title}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isCompleted && (
                <Badge className="bg-green-100 text-green-700">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Completed
                </Badge>
              )}
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>
                <Home className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Week Header */}
        <div className="mb-8">
          <Badge variant="outline" className="mb-2">Week {weekNum} of 6</Badge>
          <h1 className="text-3xl font-bold text-neutral-900 mb-2">{weekData.title}</h1>
          <p className="text-lg text-neutral-600">{weekData.subtitle}</p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="content" className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              Content
            </TabsTrigger>
            {weekData.ai_tool && (
              <TabsTrigger value="ai-tool" className="flex items-center gap-2">
                <Lightbulb className="w-4 h-4" />
                AI Tool
              </TabsTrigger>
            )}
            {generatedOutput && (
              <TabsTrigger value="output" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Output
              </TabsTrigger>
            )}
            {weekData.equipment_recommendations && (
              <TabsTrigger value="equipment" className="flex items-center gap-2">
                <Wrench className="w-4 h-4" />
                Equipment
              </TabsTrigger>
            )}
          </TabsList>

          {/* Content Tab */}
          <TabsContent value="content" className="space-y-6">
            {/* Overview Banner */}
            {weekData.detailed_training?.overview && (
              <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
                <CardContent className="pt-6">
                  <p className="text-neutral-700 text-lg leading-relaxed">{weekData.detailed_training.overview}</p>
                </CardContent>
              </Card>
            )}

            {/* Topics */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-purple-500" />
                  This Week's Topics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {weekData.topics?.map((topic, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-neutral-50 rounded-lg">
                      <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 text-sm font-medium text-purple-600">
                        {idx + 1}
                      </div>
                      <p className="text-neutral-700">{topic}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Key Concepts - Detailed Training */}
            {weekData.detailed_training?.key_concepts?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-yellow-500" />
                    Key Concepts & Training
                  </CardTitle>
                  <CardDescription>Deep dive into this week's material</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {weekData.detailed_training.key_concepts.map((concept, idx) => (
                    <div key={idx} className="border rounded-lg overflow-hidden">
                      <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-4 py-3">
                        <h4 className="font-semibold text-white">{concept.title}</h4>
                      </div>
                      <div className="p-4 space-y-3">
                        <p className="text-neutral-700 whitespace-pre-line">{concept.content}</p>
                        {concept.exercise && (
                          <div className="p-3 bg-amber-50 rounded-lg border-l-4 border-amber-400">
                            <p className="text-sm font-medium text-amber-800 mb-1">📝 Exercise</p>
                            <p className="text-sm text-amber-700">{concept.exercise}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Pro Tips */}
            {weekData.detailed_training?.pro_tips?.length > 0 && (
              <Card className="border-green-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Pro Tips
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-2">
                    {weekData.detailed_training.pro_tips.map((tip, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                        <span className="text-green-600 font-bold">✓</span>
                        <p className="text-sm text-green-800">{tip}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Common Mistakes */}
            {weekData.detailed_training?.common_mistakes?.length > 0 && (
              <Card className="border-red-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Info className="w-5 h-5 text-red-500" />
                    Common Mistakes to Avoid
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-2">
                    {weekData.detailed_training.common_mistakes.map((mistake, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
                        <span className="text-red-600 font-bold">✗</span>
                        <p className="text-sm text-red-800">{mistake}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Resources */}
            {weekData.detailed_training?.resources?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ExternalLink className="w-5 h-5 text-blue-500" />
                    Resources & Tools
                  </CardTitle>
                  <CardDescription>Helpful links for this week</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {weekData.detailed_training.resources.map((resource, idx) => (
                      <a
                        key={idx}
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors group"
                      >
                        <p className="font-medium text-blue-800 group-hover:underline flex items-center gap-2">
                          {resource.name}
                          <ExternalLink className="w-3 h-3" />
                        </p>
                        <p className="text-sm text-blue-600">{resource.description}</p>
                      </a>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Episode Frameworks (Week 3 specific) */}
            {weekData.detailed_training?.episode_frameworks?.length > 0 && (
              <Card className="border-indigo-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-indigo-500" />
                    Episode Frameworks
                  </CardTitle>
                  <CardDescription>Structures to organize your episodes</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {weekData.detailed_training.episode_frameworks.map((framework, idx) => (
                      <div key={idx} className="p-3 bg-indigo-50 rounded-lg">
                        <p className="font-medium text-indigo-800">{framework.name}</p>
                        <p className="text-sm text-indigo-600 mt-1">{framework.structure}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Budget Breakdown (Week 2 specific) */}
            {weekData.detailed_training?.budget_breakdown && (
              <Card className="border-orange-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-orange-500" />
                    Budget Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-3 gap-3">
                    {Object.entries(weekData.detailed_training.budget_breakdown).map(([tier, data]) => (
                      <div key={tier} className="p-3 bg-orange-50 rounded-lg">
                        <p className="font-medium text-orange-800 capitalize">{tier}</p>
                        <p className="text-lg font-bold text-orange-600">{data.total}</p>
                        <p className="text-sm text-orange-600 mt-1">{data.essentials}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Editing Workflow (Week 4 specific) */}
            {weekData.detailed_training?.editing_workflow && (
              <Card className="border-teal-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Play className="w-5 h-5 text-teal-500" />
                    Editing Workflow
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.entries(weekData.detailed_training.editing_workflow).map(([step, description], idx) => (
                      <div key={step} className="flex items-start gap-3 p-3 bg-teal-50 rounded-lg">
                        <div className="w-6 h-6 rounded-full bg-teal-200 flex items-center justify-center flex-shrink-0 text-sm font-bold text-teal-700">
                          {idx + 1}
                        </div>
                        <div>
                          <p className="font-medium text-teal-800 capitalize">{step.replace(/_/g, ' ')}</p>
                          <p className="text-sm text-teal-600">{description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quality Checklist (Week 4 specific) */}
            {weekData.detailed_training?.quality_checklist?.length > 0 && (
              <Card className="border-emerald-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                    Quality Checklist
                  </CardTitle>
                  <CardDescription>Review before publishing each episode</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-2">
                    {weekData.detailed_training.quality_checklist.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2 bg-emerald-50 rounded">
                        <div className="w-5 h-5 rounded border-2 border-emerald-400 flex-shrink-0" />
                        <p className="text-sm text-emerald-800">{item}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Marketing Calendar (Week 5 specific) */}
            {weekData.detailed_training?.marketing_calendar && (
              <Card className="border-pink-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-pink-500" />
                    Marketing Calendar
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(weekData.detailed_training.marketing_calendar).map(([phase, tasks]) => (
                      <div key={phase} className="p-3 bg-pink-50 rounded-lg">
                        <p className="font-medium text-pink-800 capitalize mb-2">{phase.replace(/_/g, ' ')}</p>
                        <ul className="space-y-1">
                          {tasks.map((task, idx) => (
                            <li key={idx} className="text-sm text-pink-600 flex items-start gap-2">
                              <span>•</span>
                              {task}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Monetization Timeline (Week 6 specific) */}
            {weekData.detailed_training?.monetization_timeline && (
              <Card className="border-yellow-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-yellow-600" />
                    Monetization Timeline
                  </CardTitle>
                  <CardDescription>When to implement each strategy</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(weekData.detailed_training.monetization_timeline).map(([range, strategy]) => (
                      <div key={range} className="p-3 bg-yellow-50 rounded-lg border-l-4 border-yellow-400">
                        <p className="font-medium text-yellow-800 capitalize">{range.replace(/_/g, ' ')} Downloads</p>
                        <p className="text-sm text-yellow-700">{strategy}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Rate Card Guidelines (Week 6 specific) */}
            {weekData.detailed_training?.rate_card_guidelines && (
              <Card className="border-green-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-600" />
                    Sponsorship Rate Card
                  </CardTitle>
                  <CardDescription>Industry standard CPM rates</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {Object.entries(weekData.detailed_training.rate_card_guidelines).map(([type, rate]) => (
                      <div key={type} className="p-3 bg-green-50 rounded-lg flex justify-between items-center">
                        <p className="font-medium text-green-800 capitalize">{type.replace(/_/g, ' ')}</p>
                        <Badge className="bg-green-600">{rate}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Deliverable */}
            {weekData.deliverable && (
              <Card className="border-purple-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-purple-500" />
                    Week {weekNum} Deliverable
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-neutral-700">{weekData.deliverable}</p>
                </CardContent>
              </Card>
            )}

            {/* Podcast Lingo */}
            {weekData.lingo_definitions && Object.keys(weekData.lingo_definitions).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Info className="w-5 h-5 text-blue-500" />
                    Podcast Lingo
                  </CardTitle>
                  <CardDescription>Key terms to know this week</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {Object.entries(weekData.lingo_definitions).map(([term, definition]) => (
                      <div key={term} className="p-3 bg-blue-50 rounded-lg">
                        <p className="font-medium text-blue-800">{term}</p>
                        <p className="text-sm text-blue-600">{definition}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Consultant Meeting Reminder */}
            <Card className={`border-2 ${isMeetingCompleted ? 'border-green-200 bg-green-50/50' : isMeetingScheduled ? 'border-blue-200 bg-blue-50/50' : 'border-amber-200 bg-amber-50/50'}`}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {isMeetingCompleted ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : isMeetingScheduled ? (
                    <Clock className="w-5 h-5 text-blue-600" />
                  ) : (
                    <Video className="w-5 h-5 text-amber-600" />
                  )}
                  Week {weekNum} Consultant Meeting
                  {isMeetingCompleted && (
                    <Badge className="bg-green-100 text-green-700 ml-2">Completed</Badge>
                  )}
                  {isMeetingScheduled && !isMeetingCompleted && (
                    <Badge className="bg-blue-100 text-blue-700 ml-2">Scheduled</Badge>
                  )}
                  {!isMeetingScheduled && !isMeetingCompleted && (
                    <Badge className="bg-amber-100 text-amber-700 ml-2">Recommended</Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  {isMeetingCompleted 
                    ? "You've completed your consultation for this week!"
                    : isMeetingScheduled
                    ? "Your meeting is scheduled. Your consultant will mark it complete after the session."
                    : "Schedule a 1-on-1 consultation with your consultant to review your progress and get personalized feedback."
                  }
                </CardDescription>
              </CardHeader>
              {!isMeetingCompleted && (
                <CardContent>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={handleBookMeeting}
                      className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600"
                      data-testid="book-meeting-btn"
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      {isMeetingScheduled ? "Reschedule via Square" : "Book via Square Appointments"}
                      <ExternalLink className="w-3 h-3 ml-2" />
                    </Button>
                    {!isMeetingScheduled && (
                      <Button
                        variant="outline"
                        onClick={handleMarkScheduled}
                        disabled={schedulingMeeting}
                        className="flex-1"
                        data-testid="mark-scheduled-btn"
                      >
                        {schedulingMeeting ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <CheckCircle className="w-4 h-4 mr-2" />
                        )}
                        I've Booked My Meeting
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-neutral-500 mt-3 text-center">
                    <Bell className="w-3 h-3 inline mr-1" />
                    Meetings are recommended but not required to proceed. They help maximize your success!
                  </p>
                </CardContent>
              )}
            </Card>

            {/* Exercise Completion & Strategic Plan Generator */}
            <Card className="border-[#39FF14]/20 bg-gradient-to-r from-[#39FF14]/5 to-emerald-50" data-testid="exercise-plan-card">
              <CardContent className="p-6">
                <h3 className="font-bold text-neutral-900 mb-1 flex items-center gap-2">
                  <Target className="w-5 h-5 text-[#2BC40E]" /> Week {weekNum} Exercise & Results Map
                </h3>
                <p className="text-xs text-neutral-600 mb-4">
                  Complete your exercise below, then generate a strategic implementation plan based on your answers.
                </p>

                <div className="space-y-3 mb-4">
                  <div>
                    <Label className="text-sm font-medium">What is your podcast concept or main idea?</Label>
                    <Textarea value={toolInputs.exerciseConcept || ""} onChange={e => setToolInputs({ ...toolInputs, exerciseConcept: e.target.value })}
                      placeholder="Describe your podcast concept, niche, or the topic you explored this week..." className="min-h-20 mt-1" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Key decisions or actions from this week</Label>
                    <Textarea value={toolInputs.exerciseActions || ""} onChange={e => setToolInputs({ ...toolInputs, exerciseActions: e.target.value })}
                      placeholder="What did you decide? What actions will you take? What's your plan?" className="min-h-20 mt-1" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Target audience &amp; goals</Label>
                    <Input value={toolInputs.exerciseAudience || ""} onChange={e => setToolInputs({ ...toolInputs, exerciseAudience: e.target.value })}
                      placeholder="Who is this for? What outcome do you want?" className="mt-1" />
                  </div>
                </div>

                {!toolInputs.resultsPlan ? (
                  <Button onClick={async () => {
                    if (!toolInputs.exerciseConcept) { toast.error("Fill in your concept first"); return; }
                    setGenerating(true);
                    try {
                      const res = await authFetch(`${API}/podcast/generate-results-map`, {
                        method: "POST",
                        body: JSON.stringify({
                          week: weekNum,
                          concept: toolInputs.exerciseConcept,
                          actions: toolInputs.exerciseActions || "",
                          audience: toolInputs.exerciseAudience || "",
                        }),
                      });
                      if (res.ok) {
                        const data = await res.json();
                        setToolInputs(prev => ({ ...prev, resultsPlan: data }));
                        toast.success("Strategic plan generated!");
                      } else { toast.error("Generation failed"); }
                    } catch { toast.error("Failed to generate"); }
                    setGenerating(false);
                  }} disabled={generating} className="w-full bg-[#333333] text-white h-11" data-testid="generate-plan-btn">
                    {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Target className="w-4 h-4 mr-2" />}
                    {generating ? "Generating Plan..." : "Generate My Strategic Implementation Plan"}
                  </Button>
                ) : (
                  <div className="space-y-4 mt-4" data-testid="results-map">
                    {/* Results Map Infographic */}
                    <div className="bg-[#333333] rounded-xl p-5 text-white text-center">
                      <p className="text-[#39FF14] text-xs uppercase tracking-widest mb-1">Strategic Implementation Plan</p>
                      <h4 className="text-lg font-bold">{toolInputs.resultsPlan.title || `Week ${weekNum} Results Map`}</h4>
                    </div>

                    {/* Goal */}
                    {toolInputs.resultsPlan.goal && (
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-xs font-semibold text-blue-700 uppercase mb-1">Your Goal</p>
                        <p className="text-sm text-blue-900">{toolInputs.resultsPlan.goal}</p>
                      </div>
                    )}

                    {/* Implementation Steps */}
                    {toolInputs.resultsPlan.steps?.length > 0 && (
                      <div className="space-y-2">
                        {toolInputs.resultsPlan.steps.map((step, i) => {
                          const colors = ["border-red-200 bg-red-50", "border-amber-200 bg-amber-50", "border-blue-200 bg-blue-50", "border-emerald-200 bg-emerald-50", "border-purple-200 bg-purple-50"];
                          return (
                            <div key={i} className={`p-3 rounded-lg border ${colors[i % 5]}`}>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="w-6 h-6 bg-white rounded-full flex items-center justify-center text-xs font-bold shadow-sm">{i + 1}</span>
                                <p className="font-semibold text-sm text-neutral-900">{step.action}</p>
                              </div>
                              <p className="text-xs text-neutral-600 ml-8">{step.detail}</p>
                              {step.timeline && <p className="text-[10px] text-neutral-500 ml-8 mt-0.5">Timeline: {step.timeline}</p>}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Expected Outcome */}
                    {toolInputs.resultsPlan.expected_outcome && (
                      <div className="p-4 bg-[#39FF14]/10 rounded-lg border border-[#39FF14]/30">
                        <p className="text-xs font-semibold text-[#2BC40E] uppercase mb-1">Expected Outcome</p>
                        <p className="text-sm text-neutral-900">{toolInputs.resultsPlan.expected_outcome}</p>
                      </div>
                    )}

                    {/* Key Metrics */}
                    {toolInputs.resultsPlan.metrics?.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {toolInputs.resultsPlan.metrics.map((m, i) => (
                          <div key={i} className="p-2.5 bg-neutral-50 rounded-lg border border-neutral-200 text-center">
                            <p className="text-xs font-semibold text-neutral-900">{m}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    <Button variant="outline" onClick={() => setToolInputs(prev => ({ ...prev, resultsPlan: null }))} className="w-full mt-2">
                      Regenerate Plan
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

          </TabsContent>
          {weekData.ai_tool && (
            <TabsContent value="ai-tool" className="space-y-6">
              {/* Tool Selector for weeks with multiple tools */}
              {weekData.ai_tools && weekData.ai_tools.length > 1 && (
                <div className="flex gap-2 mb-4">
                  {weekData.ai_tools.map((tool) => (
                    <Button
                      key={tool.tool}
                      variant={selectedTool === tool.tool || (!selectedTool && tool.tool === weekData.ai_tool) ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedTool(tool.tool)}
                      className={selectedTool === tool.tool || (!selectedTool && tool.tool === weekData.ai_tool) ? "bg-purple-600" : ""}
                    >
                      {tool.name}
                    </Button>
                  ))}
                </div>
              )}

              <Card className="border-purple-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-yellow-500" />
                    {weekData.ai_tools?.find(t => t.tool === selectedTool)?.name || weekData.ai_tool_name}
                  </CardTitle>
                  <CardDescription>
                    Use this AI tool to accelerate your progress
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Week-specific inputs */}
                  {weekNum === 1 && (
                    <>
                      <div className="space-y-2">
                        <Label>Your Interests (comma separated)</Label>
                        <Input
                          placeholder="e.g., entrepreneurship, marketing, personal growth"
                          value={toolInputs.interests?.join(", ") || ""}
                          onChange={(e) => setToolInputs({
                            ...toolInputs,
                            interests: e.target.value.split(",").map(s => s.trim()).filter(Boolean)
                          })}
                          data-testid="ai-interests-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Your Expertise/Background</Label>
                        <Textarea
                          placeholder="What's your professional background?"
                          value={toolInputs.expertise || ""}
                          onChange={(e) => setToolInputs({ ...toolInputs, expertise: e.target.value })}
                          rows={2}
                        />
                      </div>
                    </>
                  )}

                  {weekNum === 3 && (!selectedTool || selectedTool === "episode_title") && (
                    <>
                      <div className="space-y-2">
                        <Label>Topic/Theme for Episodes</Label>
                        <Input
                          placeholder="e.g., startup challenges, leadership lessons"
                          value={toolInputs.topic || ""}
                          onChange={(e) => setToolInputs({ ...toolInputs, topic: e.target.value })}
                          data-testid="ai-topic-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Title Style</Label>
                        <select
                          className="w-full p-2 border rounded-lg"
                          value={toolInputs.style || "engaging"}
                          onChange={(e) => setToolInputs({ ...toolInputs, style: e.target.value })}
                        >
                          <option value="engaging">Engaging & Catchy</option>
                          <option value="educational">Educational & Informative</option>
                          <option value="provocative">Provocative & Bold</option>
                          <option value="storytelling">Storytelling & Narrative</option>
                        </select>
                      </div>
                    </>
                  )}

                  {weekNum === 3 && selectedTool === "guest_outreach" && (
                    <>
                      <div className="space-y-2">
                        <Label>Guest Name</Label>
                        <Input
                          placeholder="e.g., Jane Smith"
                          value={toolInputs.guest_name || ""}
                          onChange={(e) => setToolInputs({ ...toolInputs, guest_name: e.target.value })}
                          data-testid="guest-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Guest Role/Title</Label>
                        <Input
                          placeholder="e.g., CEO of TechStartup, Author of..."
                          value={toolInputs.guest_role || ""}
                          onChange={(e) => setToolInputs({ ...toolInputs, guest_role: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Guest Expertise</Label>
                        <Textarea
                          placeholder="What are they known for? What's their expertise?"
                          value={toolInputs.guest_expertise || ""}
                          onChange={(e) => setToolInputs({ ...toolInputs, guest_expertise: e.target.value })}
                          rows={2}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Episode Topic</Label>
                        <Input
                          placeholder="What would you discuss with them?"
                          value={toolInputs.episode_topic || ""}
                          onChange={(e) => setToolInputs({ ...toolInputs, episode_topic: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Why This Guest?</Label>
                        <Textarea
                          placeholder="Why do you want them on your show?"
                          value={toolInputs.why_them || ""}
                          onChange={(e) => setToolInputs({ ...toolInputs, why_them: e.target.value })}
                          rows={2}
                        />
                      </div>
                    </>
                  )}

                  {weekNum === 4 && (
                    <>
                      <div className="space-y-2">
                        <Label>Episode Title</Label>
                        <Input
                          placeholder="What's the episode about?"
                          value={toolInputs.episode_title || ""}
                          onChange={(e) => setToolInputs({ ...toolInputs, episode_title: e.target.value })}
                          data-testid="ai-episode-title-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Episode Summary</Label>
                        <Textarea
                          placeholder="Brief description of the episode content"
                          value={toolInputs.episode_summary || ""}
                          onChange={(e) => setToolInputs({ ...toolInputs, episode_summary: e.target.value })}
                          rows={2}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Guest Name (if any)</Label>
                        <Input
                          placeholder="Leave blank for solo episode"
                          value={toolInputs.guest_name || ""}
                          onChange={(e) => setToolInputs({ ...toolInputs, guest_name: e.target.value })}
                        />
                      </div>
                    </>
                  )}

                  {weekNum === 6 && (
                    <div className="space-y-2">
                      <Label>Current Monthly Downloads</Label>
                      <Input
                        type="number"
                        placeholder="e.g., 1000"
                        value={toolInputs.monthly_downloads || ""}
                        onChange={(e) => setToolInputs({ ...toolInputs, monthly_downloads: parseInt(e.target.value) || 0 })}
                        data-testid="ai-downloads-input"
                      />
                    </div>
                  )}

                  <Button
                    onClick={handleGenerate}
                    disabled={generating}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600"
                    data-testid="generate-ai-btn"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Lightbulb className="w-4 h-4 mr-2" />
                        Generate with AI
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Output Tab */}
          {generatedOutput && (
            <TabsContent value="output" className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Generated Output</CardTitle>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(JSON.stringify(generatedOutput, null, 2))}
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Copy
                      </Button>
                      <Button variant="outline" size="sm" onClick={downloadOutput}>
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <OutputRenderer output={generatedOutput} week={weekNum} />
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Equipment Tab */}
          {weekData.equipment_recommendations && (
            <TabsContent value="equipment" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Wrench className="w-5 h-5 text-orange-500" />
                    Recommended Equipment
                  </CardTitle>
                  <CardDescription>
                    Based on your {enrollment?.intake_data?.budget || "starter"} budget for {enrollment?.podcast_type || "audio"} podcasting
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.entries(weekData.equipment_recommendations).map(([key, value]) => (
                      <div key={key} className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg">
                        <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                          <Wrench className="w-4 h-4 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-medium text-neutral-900 capitalize">
                            {key.replace(/_/g, " ")}
                          </p>
                          <p className="text-sm text-neutral-600">{value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {/* Complete Week Button */}
        <div className="mt-8 flex justify-between items-center">
          <Button
            variant="outline"
            onClick={() => weekNum > 1 && navigate(`/podcast/week/${weekNum - 1}`)}
            disabled={weekNum === 1}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous Week
          </Button>

          <div className="flex gap-3">
            {!isCompleted && (
              <Button
                onClick={handleComplete}
                disabled={completing}
                className="bg-gradient-to-r from-green-600 to-emerald-600"
                data-testid="complete-week-btn"
              >
                {completing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Completing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Complete Week {weekNum}
                  </>
                )}
              </Button>
            )}

            {weekNum < 6 && isCompleted && (
              <Button
                onClick={() => navigate(`/podcast/week/${weekNum + 1}`)}
                className="bg-gradient-to-r from-purple-600 to-pink-600"
              >
                Next Week
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

// Output Renderer Component
function OutputRenderer({ output, week }) {
  if (!output) return null;

  // Remove metadata fields
  const { output_id, user_id, tool, generated_at, _id, ...displayData } = output;

  // Week 1: Niche Finder
  if (week === 1 && output.primary_niche) {
    return (
      <div className="space-y-6">
        <div className="p-4 bg-purple-50 rounded-lg">
          <h3 className="font-bold text-lg text-purple-800 mb-2">Your Podcast Niche</h3>
          <p className="text-purple-700">{output.primary_niche}</p>
        </div>

        <div className="p-4 bg-neutral-50 rounded-lg">
          <h4 className="font-medium mb-2">Niche Statement</h4>
          <p className="text-neutral-700">{output.niche_statement}</p>
        </div>

        {output.sub_niches?.length > 0 && (
          <div>
            <h4 className="font-medium mb-2">Sub-Niche Options</h4>
            <div className="flex flex-wrap gap-2">
              {output.sub_niches.map((niche, idx) => (
                <Badge key={idx} variant="outline">{niche}</Badge>
              ))}
            </div>
          </div>
        )}

        {output.target_audience_profile && (
          <div className="p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-blue-800 mb-3">Target Audience Profile</h4>
            <div className="space-y-2 text-sm">
              <p><strong>Demographics:</strong> {output.target_audience_profile.demographics}</p>
              <p><strong>Pain Points:</strong> {output.target_audience_profile.pain_points?.join(", ")}</p>
              <p><strong>Desires:</strong> {output.target_audience_profile.desires?.join(", ")}</p>
            </div>
          </div>
        )}

        {output.show_name_ideas?.length > 0 && (
          <div>
            <h4 className="font-medium mb-2">Show Name Ideas</h4>
            <div className="grid sm:grid-cols-2 gap-2">
              {output.show_name_ideas.map((name, idx) => (
                <div key={idx} className="p-2 bg-gradient-to-r from-purple-50 to-pink-50 rounded border">
                  {name}
                </div>
              ))}
            </div>
          </div>
        )}

        {output.content_pillars?.length > 0 && (
          <div>
            <h4 className="font-medium mb-2">Content Pillars</h4>
            <div className="flex flex-wrap gap-2">
              {output.content_pillars.map((pillar, idx) => (
                <Badge key={idx} className="bg-purple-100 text-purple-700">{pillar}</Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Week 3: Episode Titles
  if (week === 3 && output.titles) {
    return (
      <div className="space-y-4">
        <h4 className="font-medium">Episode Title Ideas</h4>
        {output.titles.map((item, idx) => (
          <div key={idx} className="p-4 bg-neutral-50 rounded-lg border-l-4 border-purple-400">
            <p className="font-bold text-lg mb-1">{item.title}</p>
            <p className="text-sm text-neutral-600 mb-2">{item.episode_summary}</p>
            <p className="text-xs text-purple-600 italic">{item.hook}</p>
            {item.key_takeaways?.length > 0 && (
              <div className="mt-2">
                <p className="text-xs font-medium text-neutral-500">Key Takeaways:</p>
                <ul className="text-xs text-neutral-500 list-disc list-inside">
                  {item.key_takeaways.map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              </div>
            )}
          </div>
        ))}

        {output.title_formulas?.length > 0 && (
          <div className="mt-6">
            <h4 className="font-medium mb-2">Title Formulas to Reuse</h4>
            <ul className="space-y-1">
              {output.title_formulas.map((f, idx) => (
                <li key={idx} className="text-sm text-neutral-600 flex items-start gap-2">
                  <span className="text-purple-500">•</span> {f}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  // Week 3: Guest Outreach
  if (output.initial_email) {
    return (
      <div className="space-y-6">
        {/* Initial Email */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Send className="w-4 h-4 text-purple-500" />
              Initial Outreach Email
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="p-3 bg-purple-50 rounded-lg mb-3">
              <p className="text-sm font-medium text-purple-800">Subject: {output.initial_email.subject_line}</p>
            </div>
            <div className="whitespace-pre-wrap text-sm text-neutral-700 bg-neutral-50 p-4 rounded-lg">
              {output.initial_email.body}
            </div>
          </CardContent>
        </Card>

        {/* Follow-up Email */}
        {output.follow_up_email && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Follow-up Email (1 Week Later)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-3 bg-orange-50 rounded-lg mb-3">
                <p className="text-sm font-medium text-orange-800">Subject: {output.follow_up_email.subject_line}</p>
              </div>
              <div className="whitespace-pre-wrap text-sm text-neutral-700 bg-neutral-50 p-4 rounded-lg">
                {output.follow_up_email.body}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Social Media Messages */}
        <div className="grid sm:grid-cols-2 gap-4">
          {output.linkedin_message && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">LinkedIn Message</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-neutral-700 bg-blue-50 p-3 rounded-lg">{output.linkedin_message}</p>
              </CardContent>
            </Card>
          )}
          {output.twitter_dm && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Twitter/X DM</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-neutral-700 bg-sky-50 p-3 rounded-lg">{output.twitter_dm}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Interview Questions */}
        {output.interview_questions?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Suggested Interview Questions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {output.interview_questions.map((q, idx) => (
                  <div key={idx} className="p-3 bg-green-50 rounded-lg">
                    <p className="font-medium text-green-800">{q.question}</p>
                    <p className="text-sm text-green-600 mt-1">{q.why}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Research Questions */}
        {output.research_questions?.length > 0 && (
          <div>
            <h4 className="font-medium mb-2">Research Before Interview</h4>
            <ul className="space-y-1">
              {output.research_questions.map((q, idx) => (
                <li key={idx} className="text-sm text-neutral-600 flex items-start gap-2">
                  <span className="text-purple-500">•</span> {q}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Guest Value Proposition */}
        {output.guest_value_proposition && (
          <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg">
            <h4 className="font-medium text-purple-800 mb-2">What the Guest Gets</h4>
            <p className="text-neutral-700">{output.guest_value_proposition}</p>
          </div>
        )}

        {/* Outreach Tips */}
        {output.outreach_tips?.length > 0 && (
          <div>
            <h4 className="font-medium mb-2">Outreach Tips</h4>
            <div className="flex flex-wrap gap-2">
              {output.outreach_tips.map((tip, idx) => (
                <Badge key={idx} variant="outline" className="text-xs">{tip}</Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Week 4: Show Notes
  if (week === 4 && output.headline) {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-purple-50 rounded-lg">
          <h3 className="font-bold text-lg">{output.headline}</h3>
          <p className="text-neutral-700 mt-2">{output.intro_paragraph}</p>
        </div>

        <div>
          <h4 className="font-medium mb-2">Episode Summary</h4>
          <p className="text-neutral-700">{output.episode_summary}</p>
        </div>

        {output.timestamps?.length > 0 && (
          <div>
            <h4 className="font-medium mb-2">Timestamps</h4>
            <div className="space-y-1">
              {output.timestamps.map((ts, idx) => (
                <div key={idx} className="flex gap-3 text-sm">
                  <span className="font-mono text-purple-600">{ts.time}</span>
                  <span className="text-neutral-600">{ts.topic}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {output.key_takeaways?.length > 0 && (
          <div>
            <h4 className="font-medium mb-2">Key Takeaways</h4>
            <ul className="space-y-1">
              {output.key_takeaways.map((t, idx) => (
                <li key={idx} className="text-sm text-neutral-600 flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        )}

        {output.call_to_action && (
          <div className="p-3 bg-yellow-50 rounded-lg">
            <p className="text-sm font-medium text-yellow-800">Call to Action</p>
            <p className="text-yellow-700">{output.call_to_action}</p>
          </div>
        )}

        {output.social_media_snippet && (
          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-sm font-medium text-blue-800">Social Media Snippet</p>
            <p className="text-blue-700">{output.social_media_snippet}</p>
          </div>
        )}
      </div>
    );
  }

  // Week 5: Marketing Plan
  if (week === 5 && output.launch_strategy) {
    return (
      <div className="space-y-6">
        <div>
          <h4 className="font-bold text-lg mb-3">Launch Strategy</h4>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="p-3 bg-purple-50 rounded-lg">
              <p className="font-medium text-purple-800 mb-2">Pre-Launch</p>
              <ul className="text-sm space-y-1">
                {output.launch_strategy.pre_launch?.map((item, idx) => (
                  <li key={idx} className="text-purple-600">• {item}</li>
                ))}
              </ul>
            </div>
            <div className="p-3 bg-pink-50 rounded-lg">
              <p className="font-medium text-pink-800 mb-2">Launch Week</p>
              <ul className="text-sm space-y-1">
                {output.launch_strategy.launch_week?.map((item, idx) => (
                  <li key={idx} className="text-pink-600">• {item}</li>
                ))}
              </ul>
            </div>
            <div className="p-3 bg-orange-50 rounded-lg">
              <p className="font-medium text-orange-800 mb-2">Post-Launch</p>
              <ul className="text-sm space-y-1">
                {output.launch_strategy.post_launch?.map((item, idx) => (
                  <li key={idx} className="text-orange-600">• {item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {output.growth_tactics?.length > 0 && (
          <div>
            <h4 className="font-medium mb-2">Growth Tactics</h4>
            <div className="grid sm:grid-cols-2 gap-2">
              {output.growth_tactics.map((tactic, idx) => (
                <div key={idx} className="p-2 bg-green-50 rounded flex items-start gap-2">
                  <span className="text-green-600 font-bold">{idx + 1}.</span>
                  <span className="text-sm text-green-700">{tactic}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {output["90_day_milestones"]?.length > 0 && (
          <div>
            <h4 className="font-medium mb-2">90-Day Milestones</h4>
            <ul className="space-y-2">
              {output["90_day_milestones"].map((m, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <Target className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                  {m}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  // Week 6: Monetization
  if (week === 6 && output.monetization_readiness) {
    return (
      <div className="space-y-6">
        <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-bold text-lg">Monetization Readiness</h4>
            <Badge className="bg-green-500 text-white text-lg">{output.monetization_readiness.score}/10</Badge>
          </div>
          <p className="text-neutral-700">{output.monetization_readiness.assessment}</p>
        </div>

        {output.recommended_strategies?.length > 0 && (
          <div>
            <h4 className="font-medium mb-3">Recommended Strategies</h4>
            <div className="space-y-3">
              {output.recommended_strategies.map((strat, idx) => (
                <div key={idx} className="p-4 bg-neutral-50 rounded-lg border-l-4 border-green-400">
                  <div className="flex items-start justify-between mb-2">
                    <h5 className="font-bold">{strat.strategy}</h5>
                    <Badge variant="outline" className={
                      strat.difficulty === "easy" ? "border-green-300 text-green-600" :
                      strat.difficulty === "hard" ? "border-red-300 text-red-600" :
                      "border-yellow-300 text-yellow-600"
                    }>{strat.difficulty}</Badge>
                  </div>
                  <p className="text-sm text-neutral-600 mb-2">{strat.description}</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div><strong>Revenue:</strong> {strat.revenue_potential}</div>
                    <div><strong>Timeline:</strong> {strat.timeline}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {output.revenue_projections && (
          <div className="p-4 bg-yellow-50 rounded-lg">
            <h4 className="font-medium text-yellow-800 mb-3">Revenue Projections</h4>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-yellow-700">{output.revenue_projections["3_months"]}</p>
                <p className="text-xs text-yellow-600">3 Months</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-700">{output.revenue_projections["6_months"]}</p>
                <p className="text-xs text-yellow-600">6 Months</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-700">{output.revenue_projections["12_months"]}</p>
                <p className="text-xs text-yellow-600">12 Months</p>
              </div>
            </div>
          </div>
        )}

        {output.action_steps?.length > 0 && (
          <div>
            <h4 className="font-medium mb-2">Immediate Action Steps</h4>
            <ul className="space-y-2">
              {output.action_steps.map((step, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm p-2 bg-purple-50 rounded">
                  <Play className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                  {step}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  // Fallback: Generic renderer
  return (
    <div className="space-y-4">
      {Object.entries(displayData).map(([key, value]) => (
        <div key={key}>
          <p className="font-medium text-neutral-700 capitalize mb-1">
            {key.replace(/_/g, " ")}
          </p>
          {typeof value === "string" ? (
            <p className="text-neutral-600">{value}</p>
          ) : Array.isArray(value) ? (
            <ul className="list-disc list-inside text-sm text-neutral-600">
              {value.map((item, idx) => (
                <li key={idx}>{typeof item === "object" ? JSON.stringify(item) : item}</li>
              ))}
            </ul>
          ) : typeof value === "object" ? (
            <pre className="text-xs bg-neutral-100 p-2 rounded overflow-auto">
              {JSON.stringify(value, null, 2)}
            </pre>
          ) : (
            <p className="text-neutral-600">{String(value)}</p>
          )}
        </div>
      ))}
    </div>
  );
}

export default PodcastWeekPage;
